import React, { type FC } from 'react';
import styled, { css } from 'styled-components';
import { ColorNordicGrey2, ColorNordicGrey4, Spacing8, Spacing16 } from 'autogen/design-tokens/tokens';
import type { ProductTabTableItem, ProductTabTableModel, ProductTabType } from 'autogen/swagger/all';
import Card from 'components/generic/card';
import InlineLinkActionButton from 'components/generic/InlineButton/InlineLinkActionButton';
import GenericTable, { type ITable, type ITheme } from 'components/generic/table';
import { TableCell } from 'components/generic/table/Table';
import { Body } from 'components/generic/typography';
import format from 'helpers/translations';

const StyledCard = styled(Card)`
    margin-bottom: ${Spacing16};

    ${({ theme }) => (theme as any)?.Table?.StyledCard}
`;

const StyledTable = styled(GenericTable)<{ $useAlternativeLayout: boolean }>`
    width: 100%;

    ${({ $useAlternativeLayout }) =>
        $useAlternativeLayout &&
        css`
            td,
            th {
                min-width: 60px;

                &:first-child {
                    width: 50%; // making space for longer header in column 2
                }
            }
        `}

    ${({ theme }) => (theme as any)?.Table?.StyledTable}

    td {
        word-break: break-word;
    }
`;

const StyledBody = styled(Body)`
    margin-bottom: ${Spacing8};

    ${({ theme }) => (theme as any)?.Table?.StyledBody}
`;

const StyledCardContent = styled(Card.Content)`
    ${({ theme }) => (theme as any)?.Table?.StyledCardContent}
`;

function createTheme(useAlternativeLayout: boolean): ITheme {
    return {
        divider: {
            color: ColorNordicGrey2,
            row: true
        },
        spacing: {
            row: Spacing8,
            column: Spacing8
        },
        align: {
            base: 'left',
            lastColumn: useAlternativeLayout ? 'left' : 'right'
        }
    };
}

interface ProductTable extends Omit<ProductTabTableModel, '$type'> {
    type: ProductTabType;
}

export default function Table({ ...props }: ProductTable): JSX.Element {
    const { rows, tableHeaders, headline, type } = props;
    const useAlternativeLayout = type === 'Micronutrients' || type === 'Nutrients';

    return (
        <StyledCard backgroundColor={ColorNordicGrey4}>
            <StyledCardContent>
                {headline && (
                    <StyledBody size="M" strong>
                        {headline}
                    </StyledBody>
                )}
                <StyledTable<FC<ITable<ProductTabTableItem>>>
                    rows={rows}
                    cellRenderer={(cell) => (
                        <TableCell colSpan={cell.colspan} bold={cell.bold}>
                            {cell.linkAction ? (
                                <InlineLinkActionButton action={cell.linkAction}>
                                    {format(cell.value)}
                                </InlineLinkActionButton>
                            ) : typeof cell.value === 'string' ? (
                                format(cell.value)
                            ) : (
                                cell.value
                            )}
                        </TableCell>
                    )}
                    tableHeaders={tableHeaders}
                    themeConfig={createTheme(useAlternativeLayout)}
                    $useAlternativeLayout={useAlternativeLayout}
                />
            </StyledCardContent>
        </StyledCard>
    );
}
